import { useRef, useState, useEffect } from 'react';
import { useToast } from 'commons/hooks';
import {
  useTrackFullCatalog,
  useTranslationCatalog,
} from 'domains/Catalog/hooks';
import { useMediaUploadStatus } from '../../MediaUploadStatus';
import { ImageGalleryState } from '../ImageGallery';
import useMedia, { MediaContentType } from '../useMedia';

interface UseImageGalleryProps {
  images: ImageGalleryState[];
  isEdit?: boolean;
  onChange: (images: ImageGalleryState[], id?: string) => void;
}

interface UseImageGalleryReturn {
  // State
  totalToUpload: number;
  countLoadSuccess: number;
  countLoadError: number;
  message: string;
  videoQuantity: number;
  isShowModal: boolean;
  isOpenFileSystem: boolean;
  imageIdForEditOnOpen: string | undefined;

  // Functions
  handleOnchange: (newImages: ImageGalleryState[]) => void;
  handleOnRemove: (imageId: string) => void;
  onRetryError: () => void;
  updateImageStatus: (id: string, update: Partial<ImageGalleryState>) => void;
  handleOnCloseModal: () => void;
  handleOnOpenModal: () => void;
  handleOnEdit: (imageId: string) => void;
  onOpenEditModal: () => void;
}

function useImageGallery({
  images,
  isEdit = false,
  onChange,
}: UseImageGalleryProps): UseImageGalleryReturn {
  const sender = useTrackFullCatalog();
  const t = useTranslationCatalog();
  const { addMedia } = useMedia();
  const { addToast } = useToast();
  const imagesRef = useRef<ImageGalleryState[]>(images);
  const [totalToUpload, setTotalToUpload] = useState(0);
  const [countLoadSuccess, setCountLoadSuccess] = useState(0);
  const [countLoadError, setCountLoadError] = useState(0);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isOpenFileSystem, setIsOpenFileSystem] = useState(false);
  const [imageIdForEditOnOpen, setImageIdForEditOnOpen] = useState<
    string | undefined
  >();

  const { start: startMediaUpload, stop: stopMediaUpload } =
    useMediaUploadStatus();

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    const isNotFinishedUpload =
      totalToUpload === 0 ||
      totalToUpload !== countLoadError + countLoadSuccess;

    if (isNotFinishedUpload) return;
    resetCount();
    stopMediaUpload();
    if (countLoadError === 0) {
      addToast({
        label: t('products.toast.uploadPhotosSuccess'),
        appearance: 'success',
      });
      return;
    }
    if (countLoadSuccess === 0) {
      addToast({
        label: t('products.toast.uploadPhotosError'),
        appearance: 'danger',
      });
      return;
    }
    addToast({
      label: t('products.toast.somePhotosNotUploaded'),
      appearance: 'warning',
    });
  }, [
    totalToUpload,
    countLoadError,
    countLoadSuccess,
    t,
    addToast,
    sender,
    isEdit,
    stopMediaUpload,
  ]);

  const resetCount = () => {
    setCountLoadError(0);
    setCountLoadSuccess(0);
    setTotalToUpload(0);
  };

  const updateImageStatus = (
    id: string,
    update: Partial<ImageGalleryState>,
  ) => {
    const image = imagesRef.current.find((image) => image.id === id);
    if (!image) return;
    onChange([{ ...image, ...update }], id);
  };

  const handleAddMedia = async (media: MediaContentType) => {
    try {
      const { id, url, videoUrl = '' } = await addMedia(media);
      updateImageStatus(media.id, {
        id: `${id}`,
        ...(media.mediaType === 'video' && { src: url, videoUrl }),
        publicUrl: url,
        isError: false,
        isLoading: false,
      });
      setCountLoadSuccess((count) => count + 1);
    } catch (error) {
      setCountLoadError((count) => count + 1);
      updateImageStatus(media.id, {
        isError: true,
        isLoading: false,
      });
    }
  };

  const handleAddMedias = async (newImages: ImageGalleryState[]) => {
    if (!newImages.length) return;
    startMediaUpload();
    setTotalToUpload((total) => total + newImages.length);
    newImages.forEach((image) => {
      if (image.isEdited && !!image.base64) {
        handleAddMedia({
          id: image.id,
          base64: image.base64,
          mediaType: 'base64',
        });
        return;
      }

      if (!image.file) return;

      handleAddMedia({
        id: image.id,
        file: image.file,
        mediaType: image.mediaType,
      });
    });
  };

  const onRetryError = () => {
    const imagesWithError = imagesRef.current.filter((image) => image.isError);
    handleAddMedias(imagesWithError);
    onChange([
      ...imagesRef.current.map((image) => {
        if (image.isError) {
          return { ...image, isError: false, isLoading: true };
        }
        return image;
      }),
    ]);
  };

  const handleOnchange = (newImages: ImageGalleryState[]) => {
    const imagesWithReplacements = imagesRef.current.map((image) => {
      const replacementImage = newImages.find(
        (newImage) => image.id === newImage.replacedImageId,
      );
      return replacementImage || image;
    });

    onChange([
      ...imagesWithReplacements,
      ...newImages
        .filter((i) => i.replacedImageId === undefined)
        .map((i) => ({ ...i, alt: {} })),
    ]);
    handleAddMedias(newImages);
  };

  const handleOnRemove = (imageId: string) =>
    onChange(images.filter((image) => image.id !== imageId));

  const handleOnCloseModal = () => {
    setIsOpenFileSystem(false);
    setIsShowModal(false);
  };

  const handleOnOpenModal = () => {
    setIsShowModal(true);
  };

  const handleOnEdit = (imageId: string) => {
    setImageIdForEditOnOpen(imageId);
    handleOnOpenModal();
  };

  const onOpenEditModal = () => setImageIdForEditOnOpen(undefined);

  const message = totalToUpload
    ? t('products.detail.uploadingPhotosStatus', {
        photo: countLoadSuccess + 1,
        total: totalToUpload,
      })
    : '';

  const videoQuantity = images.filter(
    (file) => file.mediaType === 'video',
  ).length;

  return {
    // State
    totalToUpload,
    countLoadSuccess,
    countLoadError,
    message,
    videoQuantity,
    isShowModal,
    isOpenFileSystem,
    imageIdForEditOnOpen,

    // Functions
    handleOnchange,
    handleOnRemove,
    onRetryError,
    updateImageStatus,
    handleOnCloseModal,
    handleOnOpenModal,
    handleOnEdit,
    onOpenEditModal,
  };
}

export default useImageGallery;
